import { useEffect, useRef, useState } from 'react';

const Octopus = () => {
    const canvasRef = useRef(null);
    const mouseRef = useRef({ x: window.innerWidth / 2, y: window.innerHeight / 2 });
    const tentaclesRef = useRef([]);
    const animationRef = useRef(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');

        const resize = () => {
            canvas.width = window.innerWidth;
            canvas.height = window.innerHeight;
        };
        resize();
        window.addEventListener('resize', resize);

        // Initialize tentacles
        const tentacleCount = 8;
        const segmentCount = 15;

        tentaclesRef.current = Array.from({ length: tentacleCount }, (_, i) => ({
            angle: (Math.PI * 2 * i) / tentacleCount,
            segments: Array.from({ length: segmentCount }, (_, j) => ({
                x: mouseRef.current.x,
                y: mouseRef.current.y + j * 15,
                vx: 0,
                vy: 0
            }))
        }));

        const handleMouseMove = (e) => {
            mouseRef.current = { x: e.clientX, y: e.clientY };
        };
        window.addEventListener('mousemove', handleMouseMove);

        const animate = () => {
            ctx.fillStyle = 'rgba(10, 10, 30, 0.1)';
            ctx.fillRect(0, 0, canvas.width, canvas.height);

            const headX = mouseRef.current.x;
            const headY = mouseRef.current.y;
            const headRadius = 40;

            // Draw octopus head
            ctx.beginPath();
            ctx.arc(headX, headY, headRadius, 0, Math.PI * 2);
            const headGradient = ctx.createRadialGradient(
                headX - 10, headY - 10, 5,
                headX, headY, headRadius
            );
            headGradient.addColorStop(0, '#ff6b9d');
            headGradient.addColorStop(0.5, '#c44569');
            headGradient.addColorStop(1, '#8b3a62');
            ctx.fillStyle = headGradient;
            ctx.fill();

            // Draw eyes
            const eyeOffset = 12;
            const eyeRadius = 8;

            // Left eye
            ctx.beginPath();
            ctx.arc(headX - eyeOffset, headY - 8, eyeRadius, 0, Math.PI * 2);
            ctx.fillStyle = 'white';
            ctx.fill();
            ctx.beginPath();
            ctx.arc(headX - eyeOffset + 2, headY - 6, 4, 0, Math.PI * 2);
            ctx.fillStyle = '#1e1e1e';
            ctx.fill();

            // Right eye
            ctx.beginPath();
            ctx.arc(headX + eyeOffset, headY - 8, eyeRadius, 0, Math.PI * 2);
            ctx.fillStyle = 'white';
            ctx.fill();
            ctx.beginPath();
            ctx.arc(headX + eyeOffset + 2, headY - 6, 4, 0, Math.PI * 2);
            ctx.fillStyle = '#1e1e1e';
            ctx.fill();

            // Update and draw tentacles
            tentaclesRef.current.forEach((tentacle, tentacleIndex) => {
                const { angle, segments } = tentacle;

                // First segment follows the head with offset
                const baseX = headX + Math.cos(angle) * headRadius;
                const baseY = headY + Math.sin(angle) * headRadius;

                segments[0].x = baseX;
                segments[0].y = baseY;

                // Update each segment with physics
                for (let i = 1; i < segments.length; i++) {
                    const segment = segments[i];
                    const prevSegment = segments[i - 1];

                    // Gravity
                    segment.vy += 0.3;

                    // Air resistance
                    segment.vx *= 0.95;
                    segment.vy *= 0.95;

                    // Apply velocity
                    segment.x += segment.vx;
                    segment.y += segment.vy;

                    // Constraint to previous segment
                    const dx = segment.x - prevSegment.x;
                    const dy = segment.y - prevSegment.y;
                    const distance = Math.sqrt(dx * dx + dy * dy);
                    const segmentLength = 15;

                    if (distance > segmentLength) {
                        const angle = Math.atan2(dy, dx);
                        segment.x = prevSegment.x + Math.cos(angle) * segmentLength;
                        segment.y = prevSegment.y + Math.sin(angle) * segmentLength;

                        // Update velocity based on constraint
                        segment.vx = segment.x - prevSegment.x;
                        segment.vy = segment.y - prevSegment.y;
                    }

                    // Add wave motion
                    const waveOffset = Math.sin(Date.now() * 0.003 + i * 0.3 + tentacleIndex) * 3;
                    segment.x += waveOffset;
                }

                // Draw tentacle with gradient
                ctx.beginPath();
                ctx.moveTo(segments[0].x, segments[0].y);

                for (let i = 0; i < segments.length - 1; i++) {
                    const xc = (segments[i].x + segments[i + 1].x) / 2;
                    const yc = (segments[i].y + segments[i + 1].y) / 2;
                    ctx.quadraticCurveTo(segments[i].x, segments[i].y, xc, yc);
                }

                const lastSegment = segments[segments.length - 1];
                ctx.lineTo(lastSegment.x, lastSegment.y);

                const gradient = ctx.createLinearGradient(
                    baseX, baseY,
                    lastSegment.x, lastSegment.y
                );
                gradient.addColorStop(0, '#ff6b9d');
                gradient.addColorStop(0.5, '#c44569');
                gradient.addColorStop(1, '#4a1c40');

                ctx.strokeStyle = gradient;
                ctx.lineWidth = 15 - (tentacleIndex * 0.5);
                ctx.lineCap = 'round';
                ctx.lineJoin = 'round';
                ctx.stroke();

                // Draw suckers
                for (let i = 2; i < segments.length; i += 2) {
                    ctx.beginPath();
                    ctx.arc(segments[i].x, segments[i].y, 4, 0, Math.PI * 2);
                    ctx.fillStyle = 'rgba(255, 255, 255, 0.3)';
                    ctx.fill();
                }
            });

            animationRef.current = requestAnimationFrame(animate);
        };

        animate();

        return () => {
            window.removeEventListener('resize', resize);
            window.removeEventListener('mousemove', handleMouseMove);
            if (animationRef.current) {
                cancelAnimationFrame(animationRef.current);
            }
        };
    }, []);

    return (
        <div className="relative w-full h-screen bg-gradient-to-b from-[#0a0a1e] to-[#1a0a2e] overflow-hidden">
            <canvas
                ref={canvasRef}
                className="absolute inset-0"
            />
            <div className="absolute top-8 left-1/2 transform -translate-x-1/2 text-center pointer-events-none">
                <h1 className="text-6xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-pink-400 to-purple-600 mb-2">
                    Octopus
                </h1>
                <p className="text-white/60 text-sm">Move your mouse to guide the octopus</p>
            </div>
        </div>
    );
};

export default Octopus;
